/**
 * Brand emblems for the car row — «Мои авто», the car picker, the cars of one trip.
 *
 * Three rules carried over from Android's `CarBrandLogo`, each of which exists because of a
 * concrete way this looks wrong otherwise:
 *
 * - **The plate is always light**, never the current theme. Half the factory logos are black on
 *   transparent (Toyota, Peugeot, Porsche, Mercedes-Benz, Jaguar) and vanish on a dark ground;
 *   the other half arrive with an opaque white rectangle baked in (Omoda, Maybach, Lada) and
 *   would sit in a bright square on a dark card anyway.
 * - **An unknown make gets our own mark**, not a broken image and not a blank. «Марка» is a free
 *   text field: typos and rare makes land there by design.
 * - **Matching is on the string's skeleton** — lowercase, letters and digits only. So
 *   «Mercedes-Benz», «mercedes benz» and «МЕРСЕДЕС БЕНЦ» all reach the same entry. Russian
 *   spellings are listed explicitly rather than transliterated: transliteration does not work
 *   here («Лексус» becomes `leksus`, not `lexus`).
 *
 * The images are static files under /brand-logos and are loaded lazily one at a time. They are
 * deliberately NOT bundled: 142 files at ~900 KB would be dead weight in the main chunk for
 * every visitor, while a car list shows at most a handful.
 *
 * Заводы мототехники и водного транспорта здесь наравне с автомобильными: строка «Мототехника ·
 * АИ-95» несла тот же запасной знак, что лодка и бочка, пока в справочнике были одни машины.
 */

/** `Марка` as typed → the emblem file, by the make's skeleton. */
const BRAND_SLUGS: Record<string, string> = {
  lada: 'lada',
  ваз: 'lada',
  лада: 'lada',
  жигули: 'lada',
  vaz: 'lada',
  uaz: 'uaz',
  уаз: 'uaz',
  уазик: 'uaz',
  gaz: 'gaz',
  газ: 'gaz',
  газель: 'gaz',
  gazelle: 'gaz',
  zaz: 'zaz',
  заз: 'zaz',
  запорожец: 'zaz',
  aurus: 'aurus',
  аурус: 'aurus',
  evolute: 'evolute',
  эволют: 'evolute',
  sollers: 'sollers',
  соллерс: 'sollers',
  moskvich: 'moskvich',
  moskvitch: 'moskvich',
  москвич: 'moskvich',
  solaris: 'solaris',
  солярис: 'solaris',
  xcite: 'xcite',
  иксайт: 'xcite',
  amberauto: 'amberauto',
  амберавто: 'amberauto',
  kia: 'kia',
  киа: 'kia',
  кия: 'kia',
  hyundai: 'hyundai',
  хендай: 'hyundai',
  хёндай: 'hyundai',
  хундай: 'hyundai',
  хёндэ: 'hyundai',
  хендэ: 'hyundai',
  genesis: 'genesis',
  генезис: 'genesis',
  daewoo: 'daewoo',
  дэу: 'daewoo',
  деу: 'daewoo',
  дэо: 'daewoo',
  ssangyong: 'ssangyong',
  сангйонг: 'ssangyong',
  ссангйонг: 'ssangyong',
  toyota: 'toyota',
  тойота: 'toyota',
  lexus: 'lexus',
  лексус: 'lexus',
  nissan: 'nissan',
  ниссан: 'nissan',
  нисан: 'nissan',
  infiniti: 'infiniti',
  инфинити: 'infiniti',
  honda: 'honda',
  хонда: 'honda',
  acura: 'acura',
  акура: 'acura',
  mazda: 'mazda',
  мазда: 'mazda',
  mitsubishi: 'mitsubishi',
  мицубиси: 'mitsubishi',
  митсубиси: 'mitsubishi',
  мицубиши: 'mitsubishi',
  subaru: 'subaru',
  субару: 'subaru',
  suzuki: 'suzuki',
  сузуки: 'suzuki',
  судзуки: 'suzuki',
  daihatsu: 'daihatsu',
  дайхатсу: 'daihatsu',
  isuzu: 'isuzu',
  исузу: 'isuzu',
  datsun: 'datsun',
  датсун: 'datsun',
  volkswagen: 'volkswagen',
  vw: 'volkswagen',
  фольксваген: 'volkswagen',
  вольксваген: 'volkswagen',
  audi: 'audi',
  ауди: 'audi',
  bmw: 'bmw',
  бмв: 'bmw',
  бэмвэ: 'bmw',
  mercedesbenz: 'mercedes_benz',
  mercedes: 'mercedes_benz',
  мерседес: 'mercedes_benz',
  мерс: 'mercedes_benz',
  porsche: 'porsche',
  порше: 'porsche',
  opel: 'opel',
  опель: 'opel',
  mini: 'mini',
  мини: 'mini',
  smart: 'smart',
  смарт: 'smart',
  maybach: 'maybach',
  майбах: 'maybach',
  skoda: 'skoda',
  škoda: 'skoda',
  шкода: 'skoda',
  seat: 'seat',
  сеат: 'seat',
  cupra: 'cupra',
  купра: 'cupra',
  dacia: 'dacia',
  дачия: 'dacia',
  renault: 'renault',
  рено: 'renault',
  peugeot: 'peugeot',
  пежо: 'peugeot',
  citroen: 'citroen',
  citroën: 'citroen',
  ситроен: 'citroen',
  ситроён: 'citroen',
  ds: 'ds',
  dsautomobiles: 'ds',
  дс: 'ds',
  alpine: 'alpine',
  альпин: 'alpine',
  fiat: 'fiat',
  фиат: 'fiat',
  alfaromeo: 'alfa_romeo',
  альфаромео: 'alfa_romeo',
  альфа: 'alfa_romeo',
  lancia: 'lancia',
  лянча: 'lancia',
  ланча: 'lancia',
  ferrari: 'ferrari',
  феррари: 'ferrari',
  lamborghini: 'lamborghini',
  ламборгини: 'lamborghini',
  ламборджини: 'lamborghini',
  maserati: 'maserati',
  мазерати: 'maserati',
  abarth: 'abarth',
  абарт: 'abarth',
  iveco: 'iveco',
  ивеко: 'iveco',
  jeep: 'jeep',
  джип: 'jeep',
  chrysler: 'chrysler',
  крайслер: 'chrysler',
  dodge: 'dodge',
  додж: 'dodge',
  ram: 'ram',
  рам: 'ram',
  cadillac: 'cadillac',
  кадиллак: 'cadillac',
  chevrolet: 'chevrolet',
  шевроле: 'chevrolet',
  шеви: 'chevrolet',
  ford: 'ford',
  форд: 'ford',
  lincoln: 'lincoln',
  линкольн: 'lincoln',
  buick: 'buick',
  бьюик: 'buick',
  gmc: 'gmc',
  джиэмси: 'gmc',
  tesla: 'tesla',
  тесла: 'tesla',
  hummer: 'hummer',
  хаммер: 'hummer',
  pontiac: 'pontiac',
  понтиак: 'pontiac',
  volvo: 'volvo',
  вольво: 'volvo',
  saab: 'saab',
  сааб: 'saab',
  jaguar: 'jaguar',
  ягуар: 'jaguar',
  landrover: 'land_rover',
  rangerover: 'land_rover',
  лендровер: 'land_rover',
  ренджровер: 'land_rover',
  rover: 'rover',
  ровер: 'rover',
  bentley: 'bentley',
  бентли: 'bentley',
  rollsroyce: 'rolls_royce',
  роллсройс: 'rolls_royce',
  astonmartin: 'aston_martin',
  астонмартин: 'aston_martin',
  mclaren: 'mclaren',
  макларен: 'mclaren',
  lotus: 'lotus',
  лотус: 'lotus',
  haval: 'haval',
  хавал: 'haval',
  хаваль: 'haval',
  хавейл: 'haval',
  greatwall: 'great_wall',
  gwm: 'great_wall',
  грейтвол: 'great_wall',
  грейтволл: 'great_wall',
  wey: 'wey',
  вэй: 'wey',
  tank: 'tank',
  танк: 'tank',
  ora: 'ora',
  ора: 'ora',
  chery: 'chery',
  чери: 'chery',
  черри: 'chery',
  exeed: 'exeed',
  эксид: 'exeed',
  экседи: 'exeed',
  эксиид: 'exeed',
  omoda: 'omoda',
  омода: 'omoda',
  jaecoo: 'jaecoo',
  джейку: 'jaecoo',
  джекку: 'jaecoo',
  джаеку: 'jaecoo',
  geely: 'geely',
  джили: 'geely',
  джилли: 'geely',
  belgee: 'belgee',
  белджи: 'belgee',
  белджии: 'belgee',
  lynkco: 'lynk_and_co',
  lynkandco: 'lynk_and_co',
  lynk: 'lynk_and_co',
  линкэндко: 'lynk_and_co',
  zeekr: 'zeekr',
  зикр: 'zeekr',
  зикер: 'zeekr',
  livan: 'livan',
  ливан: 'livan',
  byd: 'byd',
  бид: 'byd',
  быд: 'byd',
  changan: 'changan',
  чанган: 'changan',
  чанъань: 'changan',
  deepal: 'deepal',
  дипал: 'deepal',
  avatr: 'avatr',
  аватр: 'avatr',
  gac: 'gac_group',
  гак: 'gac_group',
  джиэйси: 'gac_group',
  aion: 'aion',
  аион: 'aion',
  jetour: 'jetour',
  джетур: 'jetour',
  джитур: 'jetour',
  dongfeng: 'dongfeng',
  дунфэн: 'dongfeng',
  донгфенг: 'dongfeng',
  voyah: 'voyah',
  воях: 'voyah',
  вояж: 'voyah',
  forthing: 'forthing',
  фортинг: 'forthing',
  faw: 'faw',
  фав: 'faw',
  bestune: 'bestune',
  бестурн: 'bestune',
  бестюн: 'bestune',
  hongqi: 'hongqi',
  хунци: 'hongqi',
  хончи: 'hongqi',
  jac: 'jac',
  джак: 'jac',
  baic: 'baic_motor',
  баик: 'baic_motor',
  бэйцзи: 'baic_motor',
  soueast: 'soueast',
  соуист: 'soueast',
  kaiyi: 'kaiyi',
  кайи: 'kaiyi',
  каи: 'kaiyi',
  liauto: 'lixiang',
  lixiang: 'lixiang',
  лиавто: 'lixiang',
  лисян: 'lixiang',
  seres: 'seres',
  серес: 'seres',
  skywell: 'skywell',
  скайвелл: 'skywell',
  swm: 'swm',
  свм: 'swm',
  lifan: 'lifan',
  лифан: 'lifan',
  brilliance: 'brilliance',
  бриллианс: 'brilliance',
  zotye: 'zotye',
  зотай: 'zotye',
  haima: 'haima',
  хайма: 'haima',
  foton: 'foton',
  фотон: 'foton',
  nio: 'nio',
  нио: 'nio',
  xpeng: 'xpeng',
  икспенг: 'xpeng',
  wuling: 'wuling',
  улин: 'wuling',
  baojun: 'baojun',
  баоцзюнь: 'baojun',
  hafei: 'hafei',
  хафей: 'hafei',
  // Мототехника: мотоциклы, снегоходы, квадроциклы. Заводы здесь другие, чем у машин, и строка
  // «Мототехника · АИ-95» до сих пор шла с нашим запасным знаком — с тем же значком, что у лодки
  // и у бочки.
  yamaha: 'yamaha',
  ямаха: 'yamaha',
  kawasaki: 'kawasaki',
  кавасаки: 'kawasaki',
  кавасака: 'kawasaki',
  ktm: 'ktm',
  ктм: 'ktm',
  ducati: 'ducati',
  дукати: 'ducati',
  harleydavidson: 'harley_davidson',
  harley: 'harley_davidson',
  харлей: 'harley_davidson',
  харлейдэвидсон: 'harley_davidson',
  triumph: 'triumph',
  триумф: 'triumph',
  aprilia: 'aprilia',
  априлия: 'aprilia',
  husqvarna: 'husqvarna',
  хускварна: 'husqvarna',
  vespa: 'vespa',
  веспа: 'vespa',
  piaggio: 'piaggio',
  пьяджо: 'piaggio',
  пиаджио: 'piaggio',
  cfmoto: 'cfmoto',
  сиэфмото: 'cfmoto',
  сифмото: 'cfmoto',
  polaris: 'polaris',
  поларис: 'polaris',
  arcticcat: 'arctic_cat',
  арктиккэт: 'arctic_cat',
  арктиккат: 'arctic_cat',
  // BRP — владелец Ski-Doo, Sea-Doo и Can-Am: их собственных знаков в свободном доступе нет,
  // поэтому все три ведут на знак группы, а не на пустое место.
  brp: 'brp',
  брп: 'brp',
  skidoo: 'brp',
  seadoo: 'brp',
  canam: 'brp',
  скиду: 'brp',
  сиду: 'brp',
  кэнам: 'brp',
  // Водный транспорт: подвесные моторы и катера.
  tohatsu: 'tohatsu',
  тохатсу: 'tohatsu',
  evinrude: 'evinrude',
  эвинруд: 'evinrude',
  volvopenta: 'volvo_penta',
  вольвопента: 'volvo_penta',
  bayliner: 'bayliner',
  байлайнер: 'bayliner',
  suzukimarine: 'suzuki_marine',
  сузукимарин: 'suzuki_marine',
};

/** Only letters and digits, lowercased — «Mercedes-Benz» and «мерседес бенц» collapse together. */
export function brandSkeleton(raw: string): string {
  return raw.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
}

/** Path to this make's emblem, or null when we do not have one. */
export function brandLogoUrl(brand: string): string | null {
  const slug = BRAND_SLUGS[brandSkeleton(brand)];
  return slug === undefined ? null : `/brand-logos/${slug}.webp`;
}
